import * as THREE from 'three';
import { fbm } from '../look/noise.js';

const smooth = THREE.MathUtils.smoothstep;

/**
 * Ground in code (ADR-032): a band of a polar grid around the origin, its
 * height from a function of x and z, its texture laid flat in world metres
 * so bands side by side tile as one ground. A band's inner and outer edges
 * may wander with the angle -- a jagged shoreline, not a circle -- and two
 * bands that share an edge share its vertices exactly, so there is no crack.
 */

export type Height = (x: number, z: number) => number;
export type Tint = (x: number, z: number, y: number) => THREE.Color;

export type Band = {
  /** Metres from the origin, at an angle (radians, from −z towards +x). */
  readonly inner: number | ((angle: number) => number);
  readonly outer: number | ((angle: number) => number);
  readonly rings: number;
  readonly segments: number;
  readonly height: Height;
  /** One texture repeat per this many metres. */
  readonly tile: number;
  /** Rings bunched towards the inside (1 even; 2 twice as dense near). */
  readonly bunch?: number;
  readonly tint?: Tint;
  /** Only this part of the circle, radians [from, to]; the whole of it otherwise. */
  readonly arc?: readonly [number, number];
};

const at = (edge: number | ((angle: number) => number), angle: number): number =>
  typeof edge === 'number' ? edge : edge(angle);

/** Where a grid point is: angle from −z towards +x, as the lesson's own "ahead". */
export function polar(angle: number, r: number): [number, number] {
  return [Math.sin(angle) * r, -Math.cos(angle) * r];
}

export function band(spec: Band): THREE.BufferGeometry {
  const { rings, segments, height } = spec;
  const [from, to] = spec.arc ?? [0, Math.PI * 2];
  const whole = spec.arc === undefined;
  const columns = whole ? segments : segments + 1;
  const count = columns * (rings + 1);
  const positions = new Float32Array(count * 3);
  const normals = new Float32Array(count * 3);
  const uvs = new Float32Array(count * 2);
  const colours = new Float32Array(count * 3);
  const e = 0.25;
  for (let j = 0; j < columns; j++) {
    const angle = from + ((to - from) * j) / segments;
    const r0 = at(spec.inner, angle);
    const r1 = at(spec.outer, angle);
    for (let k = 0; k <= rings; k++) {
      const t = (k / rings) ** (spec.bunch ?? 1);
      const [x, z] = polar(angle, r0 + (r1 - r0) * t);
      const y = height(x, z);
      const i = j * (rings + 1) + k;
      positions.set([x, y, z], i * 3);
      const dx = (height(x + e, z) - height(x - e, z)) / (2 * e);
      const dz = (height(x, z + e) - height(x, z - e)) / (2 * e);
      const n = new THREE.Vector3(-dx, 1, -dz).normalize();
      normals.set([n.x, n.y, n.z], i * 3);
      uvs.set([x / spec.tile, -z / spec.tile], i * 2);
      const c = spec.tint?.(x, z, y) ?? new THREE.Color(1, 1, 1);
      colours.set([c.r, c.g, c.b], i * 3);
    }
  }
  const index: number[] = [];
  for (let j = 0; j < segments; j++) {
    const a = j;
    const b = whole ? (j + 1) % segments : j + 1;
    for (let k = 0; k < rings; k++) {
      const p = a * (rings + 1) + k;
      const q = b * (rings + 1) + k;
      // Wound so the face points up (+y), seen from above.
      index.push(p, q, p + 1, q, q + 1, p + 1);
    }
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute('normal', new THREE.BufferAttribute(normals, 3));
  geometry.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
  geometry.setAttribute('color', new THREE.BufferAttribute(colours, 3));
  geometry.setIndex(index);
  geometry.computeBoundingSphere();
  return geometry;
}

/** 0 inside `a`, 1 outside `b`, smooth between. */
export const ramp = (value: number, a: number, b: number): number =>
  THREE.MathUtils.smoothstep(value, a, b);

/**
 * A noisy lathe rising from the sea: an islet, a rock stack, a far hill.
 * Faces outwards; its foot ring stays round and still under the water (no
 * gully or wander below t = 0.3, so no flaps at the waterline); the texture
 * runs along the slope in metres, so it is not stretched. The rules for
 * building a world (worlds spec) in one helper; the volcano's cone
 * (volcano.ts) follows the same rules with a crater.
 */
export function mountain(options: {
  centre: THREE.Vector3;
  radius: (t: number) => number;
  height: number;
  rings: number;
  segments: number;
  seed: number;
  from: number;
  to: number;
  tile: number;
  crater?: number;
}): THREE.BufferGeometry {
  const ridges = fbm(options.seed, 4);
  const rows = options.rings + 1;
  const columns = options.segments + 1;
  const positions = new Float32Array(rows * columns * 3);
  const uvs = new Float32Array(rows * columns * 2);
  const colours = new Float32Array(rows * columns * 3);
  for (let k = 0; k < rows; k++) {
    const t = options.from + ((options.to - options.from) * k) / options.rings;
    for (let j = 0; j < columns; j++) {
      const a = (j / options.segments) * Math.PI * 2;
      // Noise read round a circle, so the seam where the lathe closes has no crack.
      const cx = Math.cos(a);
      const sx = Math.sin(a);
      const gully = ridges(cx * 3 + 7, sx * 3 + t * 5);
      const fine = ridges(cx * 11 + 20, sx * 11 + t * 14);
      // Radial gullies: the flanks furrowed from the top down.
      const furrow = Math.abs(Math.sin(a * 23 + gully * 6)) ** 3;
      // No wander at the foot: its ring stays round, under the water.
      const free = smooth(t, 0, 0.3);
      const r =
        options.radius(t) * (1 + free * (0.2 * (gully - 0.5) + 0.05 * furrow)) +
        free * (fine - 0.5) * Math.min(7, options.radius(t) * 0.2);
      let y = t * options.height;
      y += (ridges(cx * 2 + 3, sx * 2 + t * 4) - 0.5) * options.height * 0.05 * (1 - t);
      const shade = 0.78 + 0.35 * fine - 0.12 * furrow;
      colours.set([shade, shade * 0.97, shade * 0.95], (k * columns + j) * 3);
      const i = k * columns + j;
      positions.set(
        [
          options.centre.x + Math.sin(a) * r,
          options.centre.y + y,
          options.centre.z - Math.cos(a) * r,
        ],
        i * 3,
      );
      // Along the slope, not up it: the flank's length, so the photo is not stretched.
      const slope = Math.hypot(options.radius(0) - options.radius(t), t * options.height);
      uvs.set([(a * options.radius(t)) / options.tile, slope / options.tile], i * 2);
    }
  }
  const index: number[] = [];
  for (let k = 0; k < options.rings; k++) {
    for (let j = 0; j < options.segments; j++) {
      const p = k * columns + j;
      const q = p + columns;
      // Wound so the faces look outwards.
      index.push(p, q, p + 1, q, q + 1, p + 1);
    }
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
  geometry.setAttribute('color', new THREE.BufferAttribute(colours, 3));
  geometry.setIndex(index);
  geometry.computeVertexNormals();
  return geometry;
}

/**
 * Ground on a rectangular grid (part 2, 29 Sep 2026): for land laid out in
 * straight lines -- a dike running to the horizon, a polder's parcels, a
 * street -- which a polar band would fray at a distance. Lines at `xs` and
 * `zs` (sorted, metres; denser where the land changes), heights from a
 * function, the texture laid flat in metres. Cells wholly inside `hole`
 * metres of the origin are left out: the stage's own round ground is there,
 * and the grid's cells overlapping its edge lie behind it (polygon offset).
 */
export function grid(spec: {
  readonly xs: readonly number[];
  readonly zs: readonly number[];
  readonly height: Height;
  readonly tile: number;
  readonly tint?: Tint;
  readonly hole?: number;
}): THREE.BufferGeometry {
  const { xs, zs, height } = spec;
  const columns = xs.length;
  const count = columns * zs.length;
  const positions = new Float32Array(count * 3);
  const normals = new Float32Array(count * 3);
  const uvs = new Float32Array(count * 2);
  const colours = new Float32Array(count * 3);
  const e = 0.25;
  zs.forEach((z, j) => {
    xs.forEach((x, i) => {
      const k = j * columns + i;
      const y = height(x, z);
      positions.set([x, y, z], k * 3);
      const dx = (height(x + e, z) - height(x - e, z)) / (2 * e);
      const dz = (height(x, z + e) - height(x, z - e)) / (2 * e);
      const n = new THREE.Vector3(-dx, 1, -dz).normalize();
      normals.set([n.x, n.y, n.z], k * 3);
      uvs.set([x / spec.tile, -z / spec.tile], k * 2);
      const c = spec.tint?.(x, z, y) ?? new THREE.Color(1, 1, 1);
      colours.set([c.r, c.g, c.b], k * 3);
    });
  });
  const hole = spec.hole ?? 0;
  const inside = (i: number, j: number): boolean => Math.hypot(xs[i]!, zs[j]!) < hole;
  const index: number[] = [];
  for (let j = 0; j < zs.length - 1; j++) {
    for (let i = 0; i < columns - 1; i++) {
      if (inside(i, j) && inside(i + 1, j) && inside(i, j + 1) && inside(i + 1, j + 1)) continue;
      const a = j * columns + i;
      const b = a + 1;
      const c = a + columns;
      const d = c + 1;
      // Wound so the face points up (+y).
      index.push(a, c, b, b, c, d);
    }
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute('normal', new THREE.BufferAttribute(normals, 3));
  geometry.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
  geometry.setAttribute('color', new THREE.BufferAttribute(colours, 3));
  geometry.setIndex(index);
  geometry.computeBoundingSphere();
  return geometry;
}

/**
 * Grid lines from `from` to `to`: every `step` metres out to `near`, then
 * each gap `grow` times the last. Symmetric about 0 when `from` is −`to`.
 */
export function lines(from: number, to: number, step: number, near: number, grow = 1.12): number[] {
  const out = new Set<number>([0]);
  const reach = Math.max(Math.abs(from), Math.abs(to));
  for (const sign of [1, -1]) {
    let x = 0;
    let gap = step;
    while (x < reach) {
      x += gap;
      if (x > near) gap *= grow;
      out.add(Math.round(sign * x * 1000) / 1000);
    }
  }
  out.add(from);
  out.add(to);
  return [...out].filter((v) => v >= from && v <= to).sort((a, b) => a - b);
}
